import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Grid,
  LinearProgress,
} from '@mui/material';
import {
  People as PeopleIcon,
  Add as AddIcon,
  ContentCopy as CopyIcon,
  Email as EmailIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { Project } from '../../services/projectService';
import { builderInvitationService, BuilderInvitation, ProjectQuote } from '../../services/builderInvitationService';
import { contractService, Contract } from '../../services/contractService';

interface BuilderInvitationTabProps {
  project: Project;
}

const BuilderInvitationTab: React.FC<BuilderInvitationTabProps> = ({ project }) => {
  const [invitations, setInvitations] = useState<BuilderInvitation[]>([]);
  const [quotes, setQuotes] = useState<ProjectQuote[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [builderEmail, setBuilderEmail] = useState('');
  const [generatedCode, setGeneratedCode] = useState<{ code: string; expiresAt: string } | null>(null);
  const [selectedQuote, setSelectedQuote] = useState<ProjectQuote | null>(null);
  const [quoteDetailsOpen, setQuoteDetailsOpen] = useState(false);
  const [acceptedQuote, setAcceptedQuote] = useState<ProjectQuote | null>(null);
  const [generatingContract, setGeneratingContract] = useState(false);
  const [contractProgress, setContractProgress] = useState(0);
  const [contract, setContract] = useState<Contract | null>(null);
  const [contractError, setContractError] = useState('');

  const canInviteBuilders = (project.status === 'sow_ready' || project.status === 'quotes_received' || project.status === 'contract_ready');

  useEffect(() => {
    if (canInviteBuilders) {
      loadInvitations();
      loadQuotes();
    }
  }, [project.id, canInviteBuilders]);

  const loadInvitations = async () => {
    try {
      const data = await builderInvitationService.getProjectInvitations(project.id);
      setInvitations(data);
    } catch (err: any) {
      setError('Failed to load invitations');
    }
  };

  const loadQuotes = async () => {
    try {
      const data = await builderInvitationService.getProjectQuotes(project.id);
      setQuotes(data);
    } catch (err: any) {
      console.error('Failed to load quotes:', err);
    }
  };

  const handleRefresh = async () => {
    setLoading(true);
    setError('');
    try {
      // Reload both invitations and quotes to get the latest status
      await Promise.all([loadInvitations(), loadQuotes()]);
    } catch (err) {
      console.error('Refresh failed', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInviteBuilder = async () => {
    if (!builderEmail.trim()) return;

    setLoading(true);
    setError('');

    try {
      const result = await builderInvitationService.generateInvitation(project.id, builderEmail.trim());
      setGeneratedCode({ code: result.invitationCode, expiresAt: result.expiresAt });
      setBuilderEmail('');
      await loadInvitations();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to generate invitation');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'success';
      case 'pending': return 'warning';
      case 'expired': return 'error';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount: number | undefined) => {
    if (amount === undefined || isNaN(amount)) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP'
    }).format(amount);
  };

  const calculateQuoteTotal = (quote: ProjectQuote): number => {
    // Use totalPrice if available (from detailed quote)
    if (quote.totalPrice !== undefined) {
      return quote.totalPrice;
    }
    // Otherwise calculate from labor + materials
    return (quote.laborCost || 0) + (quote.materialsCost || 0);
  };

  const handleViewDetails = (quote: ProjectQuote) => {
    setSelectedQuote(quote);
    setQuoteDetailsOpen(true);
  };

  const handleAcceptQuote = (quote: ProjectQuote) => {
    setAcceptedQuote(quote);
  };

  const handleGenerateContract = async () => {
    if (!acceptedQuote) return;

    setGeneratingContract(true);
    setContractProgress(0);
    setContractError('');

    try {
      // Start contract generation
      const { contractId } = await contractService.generateContract(project.id, acceptedQuote.id);

      // Poll for completion
      const generatedContract = await contractService.pollContractStatus(
        project.id,
        contractId,
        (progress) => setContractProgress(progress)
      );

      setContract(generatedContract);
    } catch (err: any) {
      setContractError(err.message || 'Failed to generate contract');
    } finally {
      setGeneratingContract(false);
    }
  };

  const handleEmailContract = () => {
    if (!contract || !acceptedQuote) return;

    const subject = `Building Contract - ${project.projectType}`;
    const body = `Please find attached the building contract for the project at ${project.propertyAddress.line1}.\n\nContract ID: ${contract.id}\nTotal Value: ${formatCurrency(calculateQuoteTotal(acceptedQuote))}\n\nPlease review and sign the contract.`;

    window.open(`mailto:${acceptedQuote.builderEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  return (
    <Box>
      {/* Invitation Section */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PeopleIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Builder Invitations</Typography>
            </Box>
            {/* Right side actions group */}
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Refresh status">
                <span> {/* Span needed for Tooltip to work on disabled button */}
                  <IconButton 
                    onClick={handleRefresh} 
                    disabled={loading}
                    color="primary"
                  >
                    <RefreshIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => setInviteDialogOpen(true)}
                disabled={!canInviteBuilders}
              >
                Invite Builder
              </Button>
            </Box>
          </Box>

          {!canInviteBuilders ? (
            <Alert severity="warning" sx={{ mb: 3 }}>
              Builders can be invited once the Scope of Work has been generated and finalised.
            </Alert>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Invite qualified builders to submit quotes for your project. Each invitation includes a secure access code.
            </Typography>
          )}

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Invitations Table */}
          {invitations.length > 0 && (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Builder Email</TableCell>
                    <TableCell>Invitation Code</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Expires</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {invitations.map((invitation) => (
                    <TableRow key={invitation.id}>
                      <TableCell>{invitation.builderEmail}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" fontFamily="monospace">
                            {invitation.invitationCode}
                          </Typography>
                          <Tooltip title="Copy code">
                            <IconButton size="small" onClick={() => copyToClipboard(invitation.invitationCode)}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={invitation.status}
                          color={getStatusColor(invitation.status) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(invitation.createdAt)}</TableCell>
                      <TableCell>{formatDate(invitation.expiresAt)}</TableCell>
                      <TableCell>
                        <Tooltip title="Send email reminder">
                          <IconButton size="small">
                            <EmailIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* Quotes Section */}
      {quotes.length > 0 && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Received Quotes ({quotes.length})
            </Typography>

            <Grid container spacing={2}>
              {quotes.map((quote) => {
                const isAccepted = acceptedQuote?.id === quote.id;
                return (
                  <Grid item xs={12} md={6} key={quote.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        border: isAccepted ? 2 : 1,
                        borderColor: isAccepted ? 'success.main' : 'divider',
                        bgcolor: isAccepted ? 'success.50' : 'background.paper',
                      }}
                    >
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Typography variant="h6">
                            {formatCurrency(calculateQuoteTotal(quote))}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            {isAccepted && (
                              <Chip
                                label="Accepted"
                                color="success"
                                size="small"
                              />
                            )}
                            <Chip
                              label={quote.status}
                              color={quote.status === 'submitted' ? 'primary' : 'default'}
                              size="small"
                            />
                          </Box>
                        </Box>

                        {quote.builderEmail && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Builder: {quote.builderEmail}
                          </Typography>
                        )}

                        {quote.timeline && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Timeline: {quote.timeline}
                          </Typography>
                        )}

                        {quote.description && (
                          <Typography variant="body2" sx={{ mb: 2 }}>
                            {quote.description}
                          </Typography>
                        )}

                        {quote.items && (
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {quote.items.length} line items
                          </Typography>
                        )}

                        {quote.laborCost !== undefined && quote.materialsCost !== undefined && (
                          <>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                              <Typography variant="body2">Labor:</Typography>
                              <Typography variant="body2">{formatCurrency(quote.laborCost)}</Typography>
                            </Box>

                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="body2">Materials:</Typography>
                              <Typography variant="body2">{formatCurrency(quote.materialsCost)}</Typography>
                            </Box>
                          </>
                        )}

                        <Typography variant="caption" color="text.secondary">
                          Submitted: {formatDate(quote.submittedAt)}
                        </Typography>

                        <Box sx={{ mt: 2 }}>
                          <Button
                            variant="outlined"
                            size="small"
                            sx={{ mr: 1 }}
                            onClick={() => handleViewDetails(quote)}
                          >
                            View Details
                          </Button>
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleAcceptQuote(quote)}
                            disabled={acceptedQuote !== null}
                          >
                            {isAccepted ? 'Accepted' : 'Accept Quote'}
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Contract Section - Shows after accepting a quote */}
      {acceptedQuote && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Contract
            </Typography>

            <Alert severity="success" sx={{ mb: 3 }}>
              You have accepted the quote from {acceptedQuote.builderEmail || 'the builder'} for {formatCurrency(calculateQuoteTotal(acceptedQuote))}.
            </Alert>

            {contractError && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {contractError}
              </Alert>
            )}

            {!contract && !generatingContract && (
              <>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Generate a contract based on the accepted quote and your Statement of Work.
                </Typography>

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleGenerateContract}
                  disabled={generatingContract}
                >
                  Generate Contract
                </Button>
              </>
            )}

            {generatingContract && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Generating contract... {contractProgress}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={contractProgress}
                  sx={{ mb: 2, height: 8, borderRadius: 1 }}
                />
                <Button
                  variant="contained"
                  size="large"
                  disabled
                  sx={{ mt: 2 }}
                >
                  Generating Contract...
                </Button>
              </Box>
            )}

            {contract && !generatingContract && (
              <Box>
                <Alert severity="info" sx={{ mb: 3 }}>
                  Contract generated successfully! Review the contract below.
                </Alert>

                <Paper variant="outlined" sx={{ p: 3, mb: 3, maxHeight: 500, overflow: 'auto' }}>
                  <Typography variant="h5" gutterBottom>
                    {contract.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Contract Date: {new Date(contract.contractDate).toLocaleDateString('en-GB')}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 3 }}>
                    Generated: {new Date(contract.generatedAt).toLocaleDateString('en-GB')}
                  </Typography>

                  {contract.sections.map((section) => (
                    <Box key={section.number} sx={{ mb: 3 }}>
                      <Typography variant="h6" gutterBottom>
                        {section.number}. {section.title}
                      </Typography>
                      {section.clauses.map((clause) => (
                        <Typography key={clause.id} variant="body2" sx={{ mb: 1, pl: 2 }}>
                          {clause.id} {clause.content}
                        </Typography>
                      ))}
                    </Box>
                  ))}
                </Paper>

                <Button
                  variant="contained"
                  size="large"
                  startIcon={<EmailIcon />}
                  onClick={handleEmailContract}
                >
                  Email Contract
                </Button>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {/* Quote Details Dialog */}
      <Dialog open={quoteDetailsOpen} onClose={() => setQuoteDetailsOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Quote Details</DialogTitle>
        <DialogContent>
          {selectedQuote && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h5" gutterBottom>
                  {formatCurrency(calculateQuoteTotal(selectedQuote))}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Builder: {selectedQuote.builderEmail || 'Unknown'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Submitted: {formatDate(selectedQuote.submittedAt)}
                </Typography>
              </Box>

              {selectedQuote.items && selectedQuote.items.length > 0 ? (
                <>
                  <Typography variant="h6" gutterBottom>
                    Line Items
                  </Typography>
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Item</TableCell>
                          <TableCell>Type</TableCell>
                          <TableCell align="right">Price</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedQuote.items.map((item: any, index: number) => {
                          // Handle section-level pricing
                          if (item.sectionPrice !== undefined && item.sectionPrice !== null) {
                            return (
                              <TableRow key={index} sx={{ backgroundColor: 'action.hover' }}>
                                <TableCell colSpan={2}>
                                  <Typography variant="subtitle2">{item.content}</Typography>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2">{formatCurrency(item.sectionPrice)}</Typography>
                                </TableCell>
                              </TableRow>
                            );
                          }
                          // Handle row-level pricing
                          return (
                            <TableRow key={index}>
                              <TableCell>{item.content}</TableCell>
                              <TableCell>
                                <Chip label={item.type} size="small" />
                              </TableCell>
                              <TableCell align="right">
                                {item.price !== undefined && item.price !== null ? formatCurrency(item.price) : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        <TableRow>
                          <TableCell colSpan={2}>
                            <Typography variant="subtitle1" fontWeight="bold">Total</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="subtitle1" fontWeight="bold">
                              {formatCurrency(calculateQuoteTotal(selectedQuote))}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              ) : (
                <>
                  {selectedQuote.description && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Description</Typography>
                      <Typography variant="body2">{selectedQuote.description}</Typography>
                    </Box>
                  )}

                  {selectedQuote.timeline && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Timeline</Typography>
                      <Typography variant="body2">{selectedQuote.timeline}</Typography>
                    </Box>
                  )}

                  {selectedQuote.laborCost !== undefined && selectedQuote.materialsCost !== undefined && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="subtitle2" gutterBottom>Cost Breakdown</Typography>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Labor:</Typography>
                        <Typography variant="body2">{formatCurrency(selectedQuote.laborCost)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2">Materials:</Typography>
                        <Typography variant="body2">{formatCurrency(selectedQuote.materialsCost)}</Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                        <Typography variant="subtitle1" fontWeight="bold">Total:</Typography>
                        <Typography variant="subtitle1" fontWeight="bold">
                          {formatCurrency(calculateQuoteTotal(selectedQuote))}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQuoteDetailsOpen(false)}>Close</Button>
          {selectedQuote && (
            <Button
              variant="contained"
              onClick={() => {
                handleAcceptQuote(selectedQuote);
                setQuoteDetailsOpen(false);
              }}
            >
              Accept Quote
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Invite Builder Dialog */}
      <Dialog open={inviteDialogOpen} onClose={() => setInviteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Invite Builder</DialogTitle>
        <DialogContent>
          {generatedCode ? (
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Alert severity="success" sx={{ mb: 3 }}>
                Invitation code generated successfully!
              </Alert>

              <Typography variant="h4" sx={{ mb: 2, fontFamily: 'monospace' }}>
                {generatedCode.code}
              </Typography>

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Share this code with the builder. It expires on {formatDate(generatedCode.expiresAt)}
              </Typography>

              <Button
                variant="outlined"
                startIcon={<CopyIcon />}
                onClick={() => copyToClipboard(generatedCode.code)}
                sx={{ mr: 2 }}
              >
                Copy Code
              </Button>

              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => {
                  const subject = 'Project Invitation - Home Improvement Quote Request';
                  const body = `You've been invited to quote on a home improvement project.\n\nInvitation Code: ${generatedCode.code}\n\nThis code expires on ${formatDate(generatedCode.expiresAt)}`;
                  window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
                }}
              >
                Send Email
              </Button>
            </Box>
          ) : (
            <>
              <Typography variant="body1" sx={{ mb: 2 }}>
                Enter the builder's email address to generate a secure invitation code.
              </Typography>

              <TextField
                fullWidth
                label="Builder Email"
                type="email"
                value={builderEmail}
                onChange={(e) => setBuilderEmail(e.target.value)}
                placeholder="builder@example.com"
                sx={{ mb: 2 }}
              />

              <Alert severity="info">
                The builder will receive a unique access code that allows them to view your project and submit a quote.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setInviteDialogOpen(false);
            setGeneratedCode(null);
            setBuilderEmail('');
          }}>
            {generatedCode ? 'Close' : 'Cancel'}
          </Button>
          {!generatedCode && (
            <Button
              onClick={handleInviteBuilder}
              variant="contained"
              disabled={loading || !builderEmail.trim()}
            >
              {loading ? 'Generating...' : 'Generate Invitation'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BuilderInvitationTab;
